import { Text } from "@chakra-ui/layout";
import {
    Center,
    Flex,
    FormControl,
    FormLabel,
    Tab,
    TabIndicator,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Nullable, UUID } from "foundation-ts/types";
import { $email, $ok } from "foundation-ts/commons";
import { $trim } from "foundation-ts/strings";
import { ContactType, UserProfile } from "g1-commons/lib/doxecureTypes";
import { ContactDto, CorporationDto, UserDto } from "g1-commons/lib/doxecureClientTypes";
import { userName } from "g1-commons/lib/doxecureFunctions";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { ContactsList } from "@components/ContactsList";
import { ErrorField } from "@components/ErrorField";
import { G1Button } from "@components/G1Button";
import { G1Input } from "@components/G1Input";
import { SeaShellBox } from "@components/SeaShellBox";
import { SearchUsers } from "@components/admin/SearchUsers";
import { UserParameters } from "@components/UserInfos/UserInfos";
import { getCorporationUsers } from "@services/corporations.service";
import { addUserContact } from "@services/users.service";
import { $g1Color } from "@/theme/G1Style";
import { MandatoryEmail, NeededPersonRole } from "@utils/TypesAndConstants";
import { defaultErrorToast, defaultSuccessToast } from "@utils/toast";
import { trueOrEmptyCard, validateEmail } from "@utils/functions";
import { G1Icon } from "@utils/icons";

interface PersonPanelProps {
    profileId: UUID;
    connectedUserProfile: UserDto;
    role: NeededPersonRole;
    blacklist?: Set<UUID>;
    canaddme?: boolean;
    enableAddUserTab?: boolean;
    enableAllUsersTab?: boolean;
    onSelectPersonItemAs: (item: PersonItem, role: NeededPersonRole) => void;
    onBack: () => void;
}
export interface PersonItem {
    contactID?: UUID;
    person: UserDto;
    isConnectedUser?: boolean;
};

export type NewPersonFormValues = {
    email: string;
    lastName: string;
    firstName: string;
};

export const PersonPanel = (props: PersonPanelProps) => {
    const { profileId, role, connectedUserProfile, blacklist, canaddme = false, enableAddUserTab = true, enableAllUsersTab = true, onSelectPersonItemAs, onBack } = props;
    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<any>({
        mode: "onChange",
        reValidateMode: "onSubmit",
        defaultValues: { lastName: "", firstName: "", email: "" },
    });
    const [adds, setAdds] = useState(0) ;
    const [corporateUsers, setCorporateUsers] = useState<Nullable<UserParameters[]>>(null) ;
    const [workedCorporateUsers, setWorkedCorporateUsers] = useState<Nullable<UserParameters[]>>(null) ;
    const toast = useToast();
    const isCorporate = connectedUserProfile.userFlags?.profile === UserProfile.AdministratorCorporate || connectedUserProfile.userFlags?.profile === UserProfile.UserCorporate ;
    const roles: Record<NeededPersonRole, string[]> = {
        Sender: ["comme expéditeur", "nouvel expéditeur", "Expéditeurs"],
        Signer: ["aux signataires", "nouveau signataire", "Signataires"],
        Approver: ["", "", "Approbateurs"]
    };
    const addConnectedUserAsTitle = `M'ajouter ${roles[role][0]}`;
    const createTitleBtn = roles[role][1];
    const tabTitle = roles[role][2];
    const contactsType: Record<NeededPersonRole, ContactType> = {
        Sender: ContactType.UsualExpeditor,
        Signer: ContactType.UsualSigner,
        Approver: ContactType.UsualApprobator
    }
    const usualContactsType = contactsType[role];

    const handleSelectPersonItem = (item: PersonItem) => {
        onSelectPersonItemAs(item!, role);
    } ;

    const handleSelectContactItem = (item: ContactDto) => {
        handleSelectPersonItem({ person: item.contact, contactID: item.apid });
    } ;

    const handleSelectCollaboratorItem = (userId: UUID) => {
        const user = corporateUsers!.filter(user => user.apid === userId).first() ;
        if ($ok(user)) {
            handleSelectPersonItem({ person: user!, contactID: userId! }) ;
        }
    } ;

    const handleSearchCorporationUser = async (search?: Nullable<string>): Promise<void> => {
        const users = $ok(search) ? corporateUsers?.filter(userDto => userDto.lastName.includes(search!.toUpperCase()) || userDto.email === search?.toLocaleLowerCase()) : corporateUsers ;
        setWorkedCorporateUsers(users?.map(userDto => { return {
            firstName: userDto.firstName,
            lastName: userDto.lastName,
            email: userDto.email,
            apid: userDto.apid,
            userFlags: userDto.userFlags
        }})) ;
    } ;

    const onSubmit: SubmitHandler<NewPersonFormValues> = async (values): Promise<void> => {
        try {
            const mail = $email(values.email.toLowerCase());
            if (!$ok(mail)) { throw 'Bad email'; }
            let newContact = await addUserContact(profileId, { firstName: values.firstName, lastName: values.lastName.toUpperCase(), email: mail! });
            if (!$ok(newContact) || !$ok(newContact.apid)) {
                throw 'Bad contact creation';
            }
            toast(defaultSuccessToast(`${userName(newContact.contact)} ajouté.`));
            setAdds(adds+1) ;
            reset();
            onSelectPersonItemAs({ person: newContact.contact, contactID: newContact.apid! }, role);
        }
        catch (error) {
            toast(defaultErrorToast("Les informations renseignées ne sont pas valides."));
        }
    } ;

    useEffect(() => {
        const setCorporationUsersList = async (connectedUserId: UUID, corporations: CorporationDto[]) => {
            const users: UserParameters[] = [] ;
            for (let corporation of corporations) {
                const usersDto = await getCorporationUsers(corporation.apid) ;
                if ($ok(usersDto)) {
                    let filteredUsers = usersDto!.filter(userDto => (userDto.apid !== connectedUserId) && !blacklist?.has(userDto.apid!));
                    if (role === NeededPersonRole.Approver) {
                        filteredUsers = filteredUsers.filter(userDto => userDto.userFlags?.profile !== UserProfile.Contact);
                    }
                    users.push(...filteredUsers.map(userDto => {
                            return {
                                firstName: userDto.firstName,
                                lastName: userDto.lastName,
                                email: userDto.email,
                                apid: userDto.apid,
                                userFlags: userDto.userFlags
                            }}
                    )) ;
                }
            }
            setWorkedCorporateUsers(users) ;
            setCorporateUsers(users) ;
        }

        if (isCorporate) {
            setCorporationUsersList(connectedUserProfile.apid!, connectedUserProfile.corporations!) ;
        } else {
            setWorkedCorporateUsers([]) ;
            setCorporateUsers([]) ;
        }
    }, [isCorporate]);

    return (
        <>
            <ColumnTitle color={$g1Color('global.write')}>
                <Flex alignItems="center" as="button" onClick={onBack} width="100%">
                    <Center color={$g1Color('global.icons.navigation.previous.write')}><G1Icon.Back /></Center>
                    <Text>Retour</Text>
                </Flex>
            </ColumnTitle>
            <Tabs isFitted>
                <TabList backgroundColor={$g1Color('global.tabs.bg')} color={$g1Color('global.tabs.write')}>
                    <Tab fontWeight="semibold">{tabTitle} Usuels</Tab>
                    {role !== NeededPersonRole.Approver && <Tab fontWeight="semibold">Tous</Tab>}
                    {isCorporate && <Tab fontWeight="semibold">Collaborateurs</Tab> }
                    {role !== NeededPersonRole.Approver && <Tab fontWeight="semibold">Ajouter</Tab>}
                </TabList>
                <TabIndicator color={$g1Color('global.tabs.indicator.write')} backgroundColor={$g1Color('global.tabs.indicator.bg')} />
                <TabPanels>
                    <TabPanel>
                        { trueOrEmptyCard(canaddme,(<G1Button component="form" variant="important" title={addConnectedUserAsTitle} width="100%" mb="30px" height="70px" justifyContent="center" fontSize="lg" onClick={() => { handleSelectPersonItem({ person: connectedUserProfile!, isConnectedUser: true }); }} />)) }
                        <ContactsList connectedUserProfile={connectedUserProfile!} blacklist={blacklist} contactType={usualContactsType} additions={adds} onItemClick={handleSelectContactItem} />
                    </TabPanel>
                    {enableAllUsersTab &&
                        <TabPanel>
                            { trueOrEmptyCard(canaddme,(<G1Button component="form" variant="important" title={addConnectedUserAsTitle} width="100%" mb="30px" height="70px" justifyContent="center" fontSize="lg" onClick={() => { handleSelectPersonItem({ person: connectedUserProfile!, isConnectedUser: true }); }} />)) }
                            <ContactsList connectedUserProfile={connectedUserProfile!} blacklist={blacklist} contactType={ContactType.All} additions={adds} search ordered onItemClick={handleSelectContactItem} />
                        </TabPanel>
                    }
                    {isCorporate &&
                        <TabPanel>
                            <SearchUsers users={workedCorporateUsers || []} onSearch={handleSearchCorporationUser} onItemClick={handleSelectCollaboratorItem} />
                        </TabPanel>
                    }
                    {enableAddUserTab &&
                        <TabPanel>
                            <form noValidate onSubmit={handleSubmit(onSubmit)}>
                                <SeaShellBox component="form" spacing="5">
                                    <FormControl isRequired>
                                        <FormLabel>Nom</FormLabel>
                                        <G1Input component="form" registerInput={{ ...register("lastName", { required: "Nom obligatoire" }) }} textTransform="uppercase" />
                                        {errors.name && <ErrorField fieldName="nom" />}
                                    </FormControl>
                                    <FormControl isRequired>
                                        <FormLabel>Prénom</FormLabel>
                                        <G1Input component="form" registerInput={{ ...register("firstName", { required: "Prénom obligatoire" }) }} textTransform="capitalize" />
                                        {errors.firstName && <ErrorField fieldName="prénom" />}
                                    </FormControl>
                                    <FormControl isRequired>
                                        <FormLabel>Adresse email</FormLabel>
                                        <G1Input
                                            component="form"
                                            type="email"
                                            registerInput={{ ...register("email", {
                                                validate: value => validateEmail(value, $trim(connectedUserProfile?.email).length ? [$trim(connectedUserProfile!.email)] : []),
                                                required: MandatoryEmail
                                            }) }}
                                            textTransform="lowercase"
                                        />
                                        {$ok(errors?.email?.message) && <ErrorField fieldName="email" error={`${errors!.email!.message!}`} />}
                                    </FormControl>
                                </SeaShellBox>
                                <G1Button component="form" variant="important" title={`Créer un ${createTitleBtn}`} type="submit" marginTop="5%" width="100%" height="70px" rounded="xl" justifyContent="center" fontSize="lg" />
                            </form>
                        </TabPanel>
                    }
                </TabPanels>
            </Tabs>
        </>
    ) ;
} ;